import React, { useState, useEffect } from 'react';
import { Link } from '../routing';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
// Shared, because this page's own copy preferred `err.error` — the MACHINE code
// — over the server's sentence, so a Greek customer was shown the literal
// strings SAME_EMAIL, PASSWORD_REQUIRED and INVALID_PASSWORD.
import { errorText } from '../utils/errors';
import toast from 'react-hot-toast';
import { User, Phone, Lock, ArrowLeft, Save, Trash2, Mail, Download, Gift, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Motion';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';


export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();

  const [name, setName]           = useState(user?.full_name || '');
  const [phone, setPhone]         = useState(user?.phone || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading]     = useState(false);
  const [exporting, setExporting] = useState(false);

  // Downloaded through axios rather than a plain link because the endpoint needs
  // the bearer token, and an <a href> cannot carry one. The blob is released
  // immediately after the click so a long session does not accumulate copies of
  // the customer's own data in memory.
  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get('/auth/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `glamgo-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorText(err, t('profile.exportDataFailed')));
    } finally {
      setExporting(false);
    }
  };

  const [newEmail, setNewEmail]   = useState('');
  const [emailPw, setEmailPw]     = useState('');
  // AuthContext caches the user from login, which predates any pending change,
  // so this is read from /auth/me rather than from `user`.
  const [pendingEmail, setPendingEmail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me')
      .then((res) => { if (!cancelled) setPendingEmail(res.user?.pending_email || null); })
      // A failure here only means the pending banner is not shown; the form
      // below still works, so there is nothing worth interrupting the user for.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const requestEmailChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/email-change/request', {
        new_email: newEmail,
        password: emailPw,
      });
      // Deliberately the same message whether or not the address was free: the
      // server does not say, so that this screen cannot be used to find out
      // which addresses have accounts.
      setPendingEmail(res.pending_email || newEmail);
      setNewEmail(''); setEmailPw('');
      toast.success(t('profile.emailChangeSentToast'));
    } catch (err) {
      toast.error(errorText(err, t('profile.emailChangeFailedToast')));
    } finally {
      setLoading(false);
    }
  };

  const cancelEmailChange = async () => {
    setLoading(true);
    try {
      await api.delete('/auth/email-change');
      setPendingEmail(null);
      toast.success(t('profile.emailChangeCancelledToast'));
    } catch (err) {
      toast.error(errorText(err, t('profile.emailChangeFailedToast')));
    } finally {
      setLoading(false);
    }
  };

  const saveInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user: updated } = await api.patch('/auth/profile', { full_name: name, phone });
      setUser(updated);
      localStorage.setItem('glamgo_user', JSON.stringify(updated));
      toast.success(t('profile.savedToast'));
    } catch (err) {
      toast.error(errorText(err, t('profile.saveFailedToast')));
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) return toast.error(t('profile.passwordMismatch'));
    setLoading(true);
    try {
      // Setting a password stamps password_changed_at, and the auth middleware
      // revokes every token issued before that instant — including the one this
      // tab is holding. The server hands back a fresh token for exactly that
      // reason; discarding it meant a success toast followed by a 401 on the
      // next request, which the axios interceptor turns into a hard redirect to
      // /login. AuthContext has no token setter, so store it the same way login()
      // does.
      const { user: updated, token } = await api.patch('/auth/profile', {
        current_password: currentPw,
        new_password: newPw,
      });
      if (token) localStorage.setItem('glamgo_token', token);
      if (updated) {
        localStorage.setItem('glamgo_user', JSON.stringify(updated));
        setUser(updated);
      }
      toast.success(t('profile.passwordChangedToast'));
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(errorText(err, t('profile.passwordChangeFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body, paddingBottom: 80 }}>
      <style>{`@import url('${FONTS}');`}</style>

      <PageHero title={t('profile.title')} subtitle={user?.email} height={240} />

      {/* One entrance for the whole column rather than five staggered cards:
          this is a settings page, and every extra beat is a beat between the
          customer and the field they came here to change. */}
      <Reveal style={{ maxWidth: 600, margin: '0 auto', padding: '40px 24px 0' }}>
        <Link to="/my-rentals" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: theme.grey400, textDecoration: 'none', fontSize: 13, marginBottom: 32 }}>
          <ArrowLeft size={16} /> {t('profile.backLink')}
        </Link>

        {/* Personal info */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 32, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <User size={18} color={theme.gold} />
            <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0 }}>{t('profile.personalInfo')}</h2>
          </div>

          <form onSubmit={saveInfo}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>{t('profile.fullName')}</label>
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder={t('profile.namePlaceholder')} required />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>{t('profile.phone')}</label>
              {/* +30 is Greece. GlamGo launches in Cyprus (+357), and this number
                  is how locker pickup codes and late-return notices reach the
                  customer — the wrong hint coaches them into an unreachable one. */}
              <input value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} placeholder={t('profile.phonePlaceholder')} type="tel" />
            </div>
            <button type="submit" disabled={loading} style={btnStyle}>
              <Save size={14} /> {t('profile.save')}
            </button>
          </form>
        </div>

        {/* Refer a friend.
            The code is created on FIRST ASK rather than for every account at
            signup: a code nobody wanted is a row per customer for ever, and a
            string in the same global namespace that a real campaign code has to
            be unique against. */}
        <ReferralCard />

        {/* Change email — verified before it takes effect */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 32, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Mail size={18} color={theme.gold} />
            <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0 }}>{t('profile.changeEmail')}</h2>
          </div>
          <p style={{ fontSize: 13, color: theme.grey400, lineHeight: 1.7, marginBottom: 20 }}>
            {t('profile.changeEmailBlurb')}
          </p>

          {pendingEmail ? (
            <div style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 3, padding: 20 }}>
              <p style={{ fontSize: 13, color: theme.grey550, margin: '0 0 4px' }}>{t('profile.pendingEmail')}</p>
              <p style={{ fontSize: 15, color: theme.ink, margin: '0 0 12px' }}><strong>{pendingEmail}</strong></p>
              <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, margin: '0 0 16px' }}>
                {t('profile.pendingEmailHint')}
              </p>
              <button type="button" onClick={cancelEmailChange} disabled={loading}
                style={{ background: 'none', border: `1px solid ${theme.sand}`, padding: '10px 20px', cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: theme.font.body }}>
                {t('profile.cancelEmailChange')}
              </button>
            </div>
          ) : (
            <form onSubmit={requestEmailChange}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle} htmlFor="gg-new-email">{t('profile.newEmail')}</label>
                <input id="gg-new-email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  style={inputStyle} type="email" required autoComplete="email" />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle} htmlFor="gg-email-pw">{t('profile.currentPassword')}</label>
                <input id="gg-email-pw" value={emailPw} onChange={e => setEmailPw(e.target.value)}
                  style={inputStyle} type="password" required autoComplete="current-password" />
                <p style={{ fontSize: 12, color: theme.grey400, marginTop: 8 }}>{t('profile.changeEmailWhyPassword')}</p>
              </div>
              <button type="submit" disabled={loading} style={btnStyle}>
                <Mail size={14} /> {t('profile.sendVerification')}
              </button>
            </form>
          )}
        </div>

        {/* Change password */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 32, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <Lock size={18} color={theme.gold} />
            <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0 }}>{t('profile.changePassword')}</h2>
          </div>

          <form onSubmit={changePassword}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{t('profile.currentPassword')}</label>
              <input value={currentPw} onChange={e => setCurrentPw(e.target.value)} style={inputStyle} type="password" required />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{t('profile.newPassword')}</label>
              {/* 8, not 6: the backend rule is isLength({ min: 8 }), so a 6-char
                  password passed the browser check and came back as a bare
                  express-validator error the user could not act on. */}
              <input value={newPw} onChange={e => setNewPw(e.target.value)} style={inputStyle} type="password" required minLength={8} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>{t('profile.confirmPassword')}</label>
              <input value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={inputStyle} type="password" required minLength={8} />
            </div>
            <button type="submit" disabled={loading} style={btnStyle}>
              <Lock size={14} /> {t('profile.changeBtn')}
            </button>
          </form>
        </div>

        {/* Articles 15 and 20 GDPR — a copy of the data, in a machine-readable
            format. The Privacy Policy promised both, and the only way to ask was
            an email address on a domain that had no MX record, so neither right
            was actually exercisable. One button is cheaper than a support queue
            and cannot be broken by a bounced message. */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 32, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Download size={18} color={theme.gold} />
            <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0 }}>{t('profile.exportData')}</h2>
          </div>
          <p style={{ fontSize: 13, color: theme.grey400, lineHeight: 1.7, marginBottom: 20 }}>
            {t('profile.exportDataBlurb')}
          </p>
          <button onClick={exportData} disabled={exporting} style={{ ...btnStyle, opacity: exporting ? 0.6 : 1, cursor: exporting ? 'default' : 'pointer' }}>
            <Download size={14} /> {exporting ? t('profile.exportDataWorking') : t('profile.exportDataBtn')}
          </button>
        </div>

        {/* Deleting the account lives on its own page rather than behind a
            button here: it is irreversible, it has to explain what is kept and
            why before anything is confirmed, and Google Play requires that
            explanation to be reachable at a stable URL without signing in. */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Trash2 size={18} color={theme.redSoft} />
            <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0 }}>{t('profile.deleteAccount')}</h2>
          </div>
          <p style={{ fontSize: 13, color: theme.grey400, lineHeight: 1.7, marginBottom: 20 }}>
            {t('profile.deleteAccountBlurb')}
          </p>
          <Link
            to="/delete-account"
            style={{ display: 'inline-block', padding: '12px 28px', border: `1px solid ${theme.redBorder}`, color: theme.redSoft, textDecoration: 'none', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}
          >
            {t('profile.deleteAccountLink')}
          </Link>
        </div>
      </Reveal>
    </div>
  );
}


// ── Refer a friend ─────────────────────────────────────────────────────────

function ReferralCard() {
  const { t } = useTranslation();
  const [code, setCode] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const link = code
    ? `${window.location.origin}/r/${encodeURIComponent(code)}`
    : null;

  const create = async () => {
    setLoading(true);
    try {
      const affiliate = await api.post('/affiliates/my-code', {});
      setCode(affiliate.code);
    } catch (err) {
      toast.error(errorText(err, t('profile.referral.failed')));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Refused outright in some in-app browsers. The link is on screen either
      // way, so tell them what to do rather than fail silently.
      toast(t('profile.referral.copyManually'));
    }
  };

  return (
    <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 32, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <Gift size={18} color={theme.gold} />
        <h2 style={{ fontFamily: theme.font.display, fontSize: 22, margin: 0 }}>{t('profile.referral.title')}</h2>
      </div>
      <p style={{ fontSize: 13, color: theme.grey400, lineHeight: 1.7, marginBottom: 20 }}>
        {t('profile.referral.blurb')}
      </p>

      {!code ? (
        <button onClick={create} disabled={loading} style={btnStyle}>
          <Gift size={14} /> {loading ? t('profile.referral.creating') : t('profile.referral.create')}
        </button>
      ) : (
        <>
          <div style={{
            background: theme.creamDeep, borderRadius: 4, padding: '14px 18px',
            fontFamily: theme.font.mono, fontSize: 16, letterSpacing: '0.1em',
            color: theme.ink, textAlign: 'center', marginBottom: 12,
          }}>{code}</div>
          <div style={{ fontSize: 12.5, color: theme.grey400, wordBreak: 'break-all', marginBottom: 14 }}>{link}</div>
          <button onClick={copy} style={btnStyle}>
            <Copy size={14} /> {copied ? t('profile.referral.copied') : t('profile.referral.copyLink')}
          </button>
          <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, margin: '16px 0 0' }}>
            {t('profile.referral.note')}
          </p>
        </>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 11, color: theme.grey400, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 };
const inputStyle = { width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 2, fontSize: 14, color: theme.ink, background: theme.cream, fontFamily: theme.font.body, boxSizing: 'border-box', outline: 'none' };
const btnStyle = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: 'pointer', fontFamily: theme.font.body };
